import { useQueries, useQuery } from '@tanstack/react-query';
import { useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';

import type { StorefrontProductSummary } from '@/features/storefront/api/storefront';
import { StorefrontImage } from '@/features/storefront/components/StorefrontImage';
import { focalStyle } from '@/features/storefront/focal';
import {
  isOnSale,
  productTabId,
  productTabPanelId,
  productTabQuery,
  type ProductQueryFactory,
  type ProductTabQuery,
} from '@/features/storefront/sections/productTabs';
import type { FocalPoint, ProductTabBlock, ProductTabsSection } from '@/features/storefront/sections/schema';
import { cn } from '@/lib/cn';

/**
 * The tabs of a `product_tabs` section: which are drawn, which is open, and
 * the products under it.
 *
 * Each tab is its own listing request, run the first time the tab is opened
 * (the open tab, and any tab a shopper has clicked) and cached per tab, so a
 * row of four tabs costs one request on page load. A tab whose request comes
 * back empty is hidden on the shop - a "On sale" tab in a shop with nothing
 * marked down is a dead end - and the next tab opens in its place; once every
 * tab has come back empty, `empty` says the whole section has nothing to show.
 *
 * The customiser (`keepEmpty`) keeps every tab, because a merchant needs to see
 * the tab they are editing even while it has nothing under it.
 */
export interface ProductTabsOptions {
  /** How to ask the shop for one tab's products. Absent, `productsFor` answers instead. */
  productQuery?: ProductQueryFactory;
  /** The product grid's lookup, for a caller that has no `productQuery`. */
  productsFor: (categoryId: string | null) => StorefrontProductSummary[];
  /** Keep empty tabs, and never report the section empty. The customiser. */
  keepEmpty: boolean;
  /** Draw tabs the merchant hid, for the customiser to dim. */
  includeHidden?: boolean;
  /** What an empty tab shows instead, when the caller draws stand-ins. */
  fallback?: StorefrontProductSummary[];
  /**
   * Tabs whose products are asked for on page load, not on first open - the
   * round picture tabs that have no picture but their first product's.
   */
  eager?: (tab: ProductTabBlock) => boolean;
}

export interface ProductTabsState {
  tabs: ProductTabBlock[];
  active: ProductTabBlock | null;
  select: (id: string) => void;
  products: StorefrontProductSummary[];
  loading: boolean;
  /** True once every tab is known to hold nothing: the section draws nothing. */
  empty: boolean;
  /** Each tab's first product picture, by tab id, once its products are known. */
  pictures: Record<string, string | null>;
}

/** Without a server to ask: the product grid's own lists, sorted or narrowed here. */
function answeredLocally(
  query: ProductTabQuery,
  productsFor: (categoryId: string | null) => StorefrontProductSummary[],
): StorefrontProductSummary[] {
  const products = productsFor(query.categoryId);
  return (query.onSale ? products.filter(isOnSale) : products).slice(0, query.size);
}

export function useProductTabs(
  section: ProductTabsSection,
  {
    productQuery,
    productsFor,
    keepEmpty,
    includeHidden = false,
    fallback = [],
    eager,
  }: ProductTabsOptions,
): ProductTabsState {
  const candidates = section.blocks.filter((tab) => includeHidden || tab.visible);
  const queries = candidates.map((tab) => productTabQuery(tab, section.limit));
  const [chosen, setChosen] = useState<string | null>(null);
  // Every tab a shopper has opened keeps its request enabled, so going back to
  // one is instant - the cache answers.
  const [opened, setOpened] = useState<ReadonlySet<string>>(() => new Set());

  // One observer per tab, enabled once the tab has been clicked. A disabled
  // observer still reads its tab's cached answer, which is how a tab already
  // known to be empty is hidden before anything new is fetched.
  const results = useQueries({
    queries: candidates.map((tab, index) => {
      const query = queries[index];
      const enabled =
        query !== null &&
        productQuery !== undefined &&
        (opened.has(tab.id) || tab.id === chosen || Boolean(eager?.(tab)));
      return {
        ...(query && productQuery
          ? productQuery(query)
          : { queryKey: ['product-tab-unasked', section.id, tab.id], queryFn: async () => [] }),
        enabled,
      };
    }),
  });

  const answers = candidates.map((_, index): StorefrontProductSummary[] | undefined => {
    const query = queries[index];
    if (query === null) return [];
    if (!productQuery) return answeredLocally(query, productsFor);
    // A tab whose request failed is treated as empty: a spinner that never
    // ends is worse than a row of tabs one shorter.
    return results[index]?.isError ? [] : results[index]?.data;
  });

  const shown = keepEmpty
    ? candidates
    : candidates.filter((_, index) => {
        const answer = answers[index];
        return answer === undefined || answer.length > 0;
      });

  const active = shown.find((tab) => tab.id === chosen) ?? shown[0] ?? null;
  const activeIndex = active ? candidates.indexOf(active) : -1;
  const activeQuery = activeIndex >= 0 ? queries[activeIndex] : null;
  const answer = activeIndex >= 0 ? answers[activeIndex] : undefined;

  // The open tab may be a fallback the shopper never clicked - the first tab,
  // or the next one after an empty tab hid itself. Its request is enabled
  // here, on the same render, rather than waiting for a click.
  const asked = activeQuery && productQuery ? productQuery(activeQuery) : null;
  const activeResult = useQuery({
    queryKey: asked?.queryKey ?? ['product-tab-unasked', section.id],
    queryFn: asked?.queryFn ?? (async () => []),
    enabled: asked !== null && answer === undefined,
  });
  const fetched = asked === null ? undefined : activeResult.isError ? [] : activeResult.data;
  const products = answer ?? fetched ?? [];
  const loading = activeQuery !== null && answer === undefined && fetched === undefined;
  const pictures: Record<string, string | null> = {};
  candidates.forEach((tab, index) => {
    const list = answers[index] ?? (tab === active ? fetched : undefined) ?? [];
    pictures[tab.id] = list.find((product) => product.image_url)?.image_url ?? null;
  });

  return {
    pictures,
    tabs: shown,
    active,
    select: (id: string) => {
      setChosen(id);
      setOpened((current) => (current.has(id) ? current : new Set([...current, id])));
    },
    products: products.length === 0 && !loading ? fallback : products,
    loading,
    empty: !keepEmpty && shown.length === 0,
  };
}

/** How the strip of tabs looks, by `tab_style`. */
function tabStyle(style: ProductTabsSection['tab_style'], selected: boolean): CSSProperties {
  if (style === 'circles') {
    return { color: selected ? undefined : 'var(--storefront-muted)' };
  }
  if (style === 'pills') {
    return selected
      ? {
          backgroundColor: 'var(--storefront-primary)',
          color: 'var(--storefront-background)',
          borderRadius: '9999px',
        }
      : { backgroundColor: 'var(--storefront-surface)', borderRadius: '9999px' };
  }
  return {
    borderBottom: '2px solid',
    borderBottomColor: selected ? 'var(--storefront-primary)' : 'transparent',
    color: selected ? undefined : 'var(--storefront-muted)',
  };
}

/**
 * One `circles` tab's round picture, ringed in the primary colour while its
 * tab is open. Without a picture, the label's first letter on the surface.
 */
function CirclePicture({
  label,
  url,
  selected,
  focal,
}: {
  label: string;
  url: string | null;
  selected: boolean;
  /** Where the tab's own picture is cropped; null for a stand-in picture. */
  focal: FocalPoint | null;
}) {
  return (
    <span
      data-testid="product-tab-circle"
      className="grid size-[5.5rem] shrink-0 place-items-center overflow-hidden rounded-full transition-shadow sm:size-28"
      style={{
        backgroundColor: 'var(--storefront-surface)',
        boxShadow: selected
          ? '0 0 0 3px var(--storefront-background), 0 0 0 5px var(--storefront-primary)'
          : undefined,
      }}
    >
      {url ? (
        <StorefrontImage url={url} sizes="112px" alt="" className="size-full object-cover" style={focalStyle(focal)} />
      ) : (
        <span aria-hidden className="text-2xl font-semibold uppercase">
          {label.trim().charAt(0)}
        </span>
      )}
    </span>
  );
}

/**
 * The tab strip itself: a WAI-ARIA tablist with a roving tab stop. Arrow keys
 * move between tabs and open the one they land on, Home and End jump to the
 * ends, and only the open tab is in the page's tab order.
 */
export function ProductTabList({
  section,
  tabs,
  active,
  onSelect,
  label,
  wrapTab = (_, node) => node,
  pictureFor = () => null,
}: {
  section: ProductTabsSection;
  tabs: ProductTabBlock[];
  active: ProductTabBlock | null;
  onSelect: (id: string) => void;
  /** What a screen reader calls the strip. */
  label: string;
  /** Wraps one tab, so the customiser can outline and select it. */
  wrapTab?: (tab: ProductTabBlock, node: ReactNode) => ReactNode;
  /** The round picture a `circles` tab shows; null draws its initial instead. */
  pictureFor?: (tab: ProductTabBlock) => string | null;
}) {
  const circles = section.tab_style === 'circles';
  const move = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = tabs.length - 1;
    const next =
      event.key === 'ArrowRight'
        ? (index + 1) % tabs.length
        : event.key === 'ArrowLeft'
          ? (index - 1 + tabs.length) % tabs.length
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? last
              : null;
    if (next === null) return;
    event.preventDefault();
    const tab = tabs[next];
    onSelect(tab.id);
    document.getElementById(productTabId(section.id, tab.id))?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label={label}
      className={cn(
        'mb-6 flex gap-2 overflow-x-auto',
        section.tab_style === 'underline' && 'gap-6 border-b',
        // Centred while the row fits, scrolled sideways (from its start) when
        // it does not; the padding keeps the selected ring inside the scroller.
        circles &&
          'mb-8 gap-4 px-1.5 pt-1.5 pb-2 sm:gap-6 [&>*:first-child]:ml-auto [&>*:last-child]:mr-auto',
      )}
      style={
        section.tab_style === 'underline'
          ? { borderColor: 'color-mix(in srgb, var(--storefront-text) 12%, transparent)' }
          : undefined
      }
    >
      {tabs.map((tab, index) => {
        const selected = tab.id === active?.id;
        return (
          <span key={tab.id} className={cn('shrink-0', !tab.visible && 'opacity-40')}>
            {wrapTab(
              tab,
              <button
                type="button"
                role="tab"
                id={productTabId(section.id, tab.id)}
                aria-selected={selected}
                aria-controls={productTabPanelId(section.id)}
                tabIndex={selected ? 0 : -1}
                onClick={() => onSelect(tab.id)}
                onKeyDown={(event) => move(event, index)}
                className={cn(
                  'text-sm font-semibold transition-colors',
                  circles
                    ? 'flex w-24 flex-col items-center gap-2 text-center sm:w-28'
                    : 'whitespace-nowrap',
                  section.tab_style === 'pills' && 'px-4 py-2',
                  section.tab_style === 'underline' && '-mb-px pb-3',
                )}
                style={tabStyle(section.tab_style, selected)}
              >
                {circles ? (
                  <>
                    <CirclePicture
                      label={tab.label}
                      url={pictureFor(tab)}
                      selected={selected}
                      focal={tab.image_url && pictureFor(tab) === tab.image_url ? tab.image_focal : null}
                    />
                    <span className="line-clamp-2">{tab.label}</span>
                  </>
                ) : (
                  tab.label
                )}
              </button>,
            )}
          </span>
        );
      })}
    </div>
  );
}
